import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Box,
  Rating,
  FormControlLabel,
  Switch,
  Chip,
  Alert,
  CircularProgress,
  Autocomplete,
  Divider,
} from '@mui/material';
import { Save, Cancel } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { sourcesApi, tagsApi, authorsApi } from '../services/api';
import { Author } from '../types';
import { CreateSourceDto } from '../types';

const EditSource: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState('');
  const [existingTags, setExistingTags] = useState<string[]>([]);
  const [existingPlatforms, setExistingPlatforms] = useState<string[]>([]);
  const [existingAuthors, setExistingAuthors] = useState<Author[]>([]);
  const [showNewAuthorForm, setShowNewAuthorForm] = useState(false);
  const [newAuthorData, setNewAuthorData] = useState({ name: '', website: '', bio: '', isPublic: true });

  const [formData, setFormData] = useState<CreateSourceDto>({
    name: '',
    description: '',
    url: '',
    authorId: undefined,
    sourcePlatform: '',
    sourceDate: '',
    rating: 0,
    isFavorite: false,
    isPublic: true,
    personalNotes: '',
    tagNames: [],
  });

  const getAvailableTags = () => {
    return existingTags;
  };

  useEffect(() => {
    const fetchSource = async () => {
      if (!id) return;

      setFetchLoading(true);
      try {
        const source = await sourcesApi.getSource(parseInt(id));
        setFormData({
          name: source.name,
          description: source.description,
          url: source.url || '',
          authorId: source.author?.id,
          sourcePlatform: source.sourcePlatform || '',
          sourceDate: source.sourceDate ? source.sourceDate.split('T')[0] : '',
          rating: source.rating,
          isFavorite: source.isFavorite,
          isPublic: source.isPublic,
          personalNotes: source.personalNotes || '',
          tagNames: source.tags.map(tag => tag.name),
        });
      } catch (err) {
        console.error('Failed to fetch source:', err);
        setError('Failed to load source');
      } finally {
        setFetchLoading(false);
      }
    };

    const fetchData = async () => {
      try {
        // Fetch all tags from the backend
        const tagsResult = await tagsApi.getAllTags({ pageSize: 1000 });
        const tagSet = new Set<string>(tagsResult.data.map(tag => tag.name));

        // Fetch sources for platforms
        const result = await sourcesApi.getSources({});
        const platformSet = new Set<string>();

        result.data.forEach(source => {
          if (source.sourcePlatform) {
            platformSet.add(source.sourcePlatform);
          }
        });

        // Fetch available authors
        const authorsResult = await authorsApi.getAuthors({ includePrivate: false });

        setExistingTags(Array.from(tagSet).sort());
        setExistingPlatforms(Array.from(platformSet).sort());
        setExistingAuthors(authorsResult.data);
      } catch (err) {
        console.error('Failed to fetch tags and platforms:', err);
      }
    };

    fetchSource();
    fetchData();
  }, [id]);

  const handleInputChange = (field: keyof CreateSourceDto) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleRatingChange = (newValue: number | null) => {
    setFormData(prev => ({
      ...prev,
      rating: newValue || 0,
    }));
  };

  const handleFavoriteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      isFavorite: e.target.checked,
    }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tagNames.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tagNames: [...prev.tagNames, tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tagNames: prev.tagNames.filter(tag => tag !== tagToRemove),
    }));
  };

  const handleTagInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    if (!id) {
      setError('Invalid source ID');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await sourcesApi.updateSource(parseInt(id), {
        ...formData,
        sourceDate: formData.sourceDate || undefined,
      });
      
      navigate(`/sources/${id}`);
    } catch (err) {
      console.error('Failed to update source:', err);
      setError('Failed to update source. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/sources/${id}`);
  };

  if (fetchLoading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Typography variant="h4" component="h1" gutterBottom>
        Edit Source
      </Typography>

      <Card>
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
              </Box>

              <TextField
                fullWidth
                required
                label="Name"
                value={formData.name}
                onChange={handleInputChange('name')}
                placeholder="e.g., React Documentation, Python Tutorial"
              />

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description}
                onChange={handleInputChange('description')}
                placeholder="Brief description of the source and what it covers"
              />

              <TextField
                fullWidth
                label="URL"
                value={formData.url}
                onChange={handleInputChange('url')}
                placeholder="https://..."
              />

              <Autocomplete
                options={existingAuthors}
                getOptionLabel={(option) => option.name}
                value={existingAuthors.find(a => a.id === formData.authorId) || null}
                onChange={(event, newValue) => {
                  setFormData(prev => ({
                    ...prev,
                    authorId: newValue?.id,
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Author"
                    placeholder="Select or create author"
                  />
                )}
              />

              {!showNewAuthorForm && (
                <Button
                  variant="text"
                  onClick={() => setShowNewAuthorForm(true)}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Create New Author
                </Button>
              )}

              {showNewAuthorForm && (
                <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    New Author
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth
                      required
                      label="Author Name"
                      value={newAuthorData.name}
                      onChange={(e) => setNewAuthorData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., John Doe"
                    />
                    <TextField
                      fullWidth
                      label="Website (optional)"
                      value={newAuthorData.website}
                      onChange={(e) => setNewAuthorData(prev => ({ ...prev, website: e.target.value }))}
                      placeholder="https://..."
                    />
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Bio (optional)"
                      value={newAuthorData.bio}
                      onChange={(e) => setNewAuthorData(prev => ({ ...prev, bio: e.target.value }))}
                      placeholder="Brief description of the author"
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="contained"
                        onClick={async () => {
                          if (!newAuthorData.name.trim()) {
                            setError('Author name is required');
                            return;
                          }
                          try {
                            const createdAuthor = await authorsApi.createAuthor({
                              name: newAuthorData.name,
                              website: newAuthorData.website || undefined,
                              bio: newAuthorData.bio || undefined,
                              isPublic: newAuthorData.isPublic,
                            });
                            setExistingAuthors(prev => [...prev, createdAuthor]);
                            setFormData(prev => ({ ...prev, authorId: createdAuthor.id }));
                            setShowNewAuthorForm(false);
                            setNewAuthorData({ name: '', website: '', bio: '', isPublic: true });
                          } catch (err) {
                            console.error('Failed to create author:', err);
                            setError('Failed to create author');
                          }
                        }}
                      >
                        Create Author
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setShowNewAuthorForm(false);
                          setNewAuthorData({ name: '', website: '', bio: '', isPublic: true });
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                </Box>
              )}

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Autocomplete
                  freeSolo
                  options={existingPlatforms}
                  value={formData.sourcePlatform || ''}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({
                      ...prev,
                      sourcePlatform: newValue || '',
                    }));
                  }}
                  onInputChange={(event, newInputValue) => {
                    setFormData(prev => ({
                      ...prev,
                      sourcePlatform: newInputValue || '',
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Source Platform"
                      placeholder="e.g., YouTube, Wikipedia, Medium"
                    />
                  )}
                />

                <TextField
                  fullWidth
                  label="Source Date"
                  type="date"
                  value={formData.sourceDate || ''}
                  onChange={handleInputChange('sourceDate')}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Rating & Preferences
                </Typography>
              </Box>

              <Box>
                <Typography component="legend" gutterBottom>
                  Rating
                </Typography>
                <Rating
                  value={formData.rating}
                  onChange={(_, newValue) => handleRatingChange(newValue)}
                  size="large"
                />
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isFavorite}
                    onChange={handleFavoriteChange}
                  />
                }
                label="Mark as Favorite"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isPublic}
                    onChange={(e) => setFormData(prev => ({ ...prev, isPublic: e.target.checked }))}
                  />
                }
                label="Public (visible to all users)"
              />

              <Box>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Tags
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <TextField
                  label="Add Tag"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={handleTagInputKeyPress}
                  placeholder="Type to search or create new tag"
                  size="small"
                  helperText="Choose from existing tags or create new ones"
                />
                <Button
                  variant="outlined"
                  onClick={handleAddTag}
                  disabled={!tagInput.trim() || formData.tagNames.includes(tagInput.trim())}
                >
                  Add
                </Button>
              </Box>

              {tagInput && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Suggested tags:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {getAvailableTags()
                      .filter(tag => 
                        tag.toLowerCase().includes(tagInput.toLowerCase()) &&
                        !formData.tagNames.includes(tag)
                      )
                      .map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              tagNames: [...prev.tagNames, tag],
                            }));
                            setTagInput('');
                          }}
                          sx={{ cursor: 'pointer' }}
                          variant="outlined"
                          color="primary"
                        />
                      ))}
                  </Box>
                </Box>
              )}

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.tagNames.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    onDelete={() => handleRemoveTag(tag)}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Personal Notes
                </Typography>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Personal Notes"
                value={formData.personalNotes}
                onChange={handleInputChange('personalNotes')}
                placeholder="Your thoughts, key takeaways, or notes about this source"
              />

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Save />}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default EditSource;